import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import {
  ITemplatePreviewDialogData,
  TemplatePreviewDialogComponent,
} from '@principle-theorem/ng-templating';
import {
  AutomatedFormIssue,
  AutomatedNotification,
  AutomationConfiguration,
  AutomationDisplay,
  GeneratedTask,
  TemplateDefinition,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  AnyAutomation,
  AnyAutomationConfiguration,
  AutomatedNotificationType,
  AutomationContext,
  AutomationCreator,
  IAutomatedFormIssue,
  IAutomatedFormIssueConfiguration,
  IAutomatedNotificationConfiguration,
  IAutomationResource,
  IAutomationScopeRequirements,
  IBrand,
  IGeneratedTaskConfiguration,
  IStaffer,
  TemplateType,
  isAutomatedFormIssueConfiguration,
  isAutomatedNotification,
  isAutomatedNotificationConfiguration,
  isGeneratedTask,
  isGeneratedTaskConfiguration,
  type IAutomatedNotification,
  type IAutomation,
  type IAutomationConfiguration,
  type IEvent,
  type IGeneratedTask,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithRef,
  filterUndefined,
  isWithRef,
  snapshot,
  snapshotDefined,
  type DocumentReference,
  type Timestamp,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { from, type Observable } from 'rxjs';
import { concatMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  AutomatedFormIssueDialogComponent,
  IAutomatedFormIssueDialogData,
  IAutomatedFormIssueReturnData,
} from '../components/automated-form-issue-dialog/automated-form-issue-dialog.component';
import { IAutomatedNotificationReturnData } from '../components/automated-notification-edit/automated-notification-edit.component';
import {
  GeneratedTaskDialogComponent,
  type IGeneratedTaskDialogData,
  type IGeneratedTaskReturnData,
} from '../components/generated-task-dialog/generated-task-dialog.component';
import {
  NotificationDialogComponent,
  type IAutomatedNotificationDialogData,
} from '../components/notification-dialog/notification-dialog.component';
import {
  ISelectAutomationTemplateResponse,
  SelectAutomationConfigurationDialogComponent,
  type ISelectAutomationTemplateRequest,
} from '../components/select-automation-configuration-dialog/select-automation-configuration-dialog.component';

@Injectable()
export class AutomationDialogService {
  constructor(
    private _dialog: MatDialog,
    private _organisation: OrganisationService,
    private _snackBar: MatSnackBar
  ) {}

  async selectConfiguration(
    request: ISelectAutomationTemplateRequest
  ): Promise<ISelectAutomationTemplateResponse | undefined> {
    return this._dialog
      .open<
        SelectAutomationConfigurationDialogComponent,
        ISelectAutomationTemplateRequest,
        ISelectAutomationTemplateResponse
      >(
        SelectAutomationConfigurationDialogComponent,
        DialogPresets.large({ height: '80%', data: request })
      )
      .afterClosed()
      .toPromise();
  }

  async openTaskDialog(
    data: IGeneratedTaskDialogData
  ): Promise<IGeneratedTaskReturnData | undefined> {
    return this._dialog
      .open<
        GeneratedTaskDialogComponent,
        IGeneratedTaskDialogData,
        IGeneratedTaskReturnData
      >(
        GeneratedTaskDialogComponent,
        DialogPresets.large({
          autoFocus: true,
          height: '80%',
          data,
        })
      )
      .afterClosed()
      .toPromise();
  }

  async openAutomatedFormIssueDialog(
    data: IAutomatedFormIssueDialogData
  ): Promise<IAutomatedFormIssueReturnData | undefined> {
    return this._dialog
      .open<
        AutomatedFormIssueDialogComponent,
        IAutomatedFormIssueDialogData,
        IAutomatedFormIssueReturnData
      >(
        AutomatedFormIssueDialogComponent,
        DialogPresets.large({
          autoFocus: true,
          height: '80%',
          data,
        })
      )
      .afterClosed()
      .toPromise();
  }

  async openTaskAdd(
    event: IEvent,
    scopeData: IAutomationScopeRequirements,
    context: AutomationContext,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<IGeneratedTask> | undefined> {
    const generatedTask = await this.openTaskDialog({
      triggerDate: event.from,
      useRelativeTime: true,
      context,
    });

    return this._toTaskAutomation(
      event,
      scopeData,
      triggerAfterDate,
      generatedTask
    );
  }

  async openTaskEdit(
    task: IGeneratedTask,
    event: IEvent,
    scopeData: IAutomationScopeRequirements,
    context: AutomationContext,
    triggerDate?: Timestamp,
    triggerAfterDate?: Timestamp,
    useRelativeTime: boolean = true
  ): Promise<IAutomation<IGeneratedTask> | undefined> {
    const generatedTask = await this.openTaskDialog({
      task,
      triggerDate,
      useRelativeTime,
      context,
    });
    return this._toTaskAutomation(
      event,
      scopeData,
      triggerAfterDate,
      generatedTask
    );
  }

  async openNotificationDialog(
    data: IAutomatedNotificationDialogData,
    event: IEvent,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<IAutomatedNotification> | undefined> {
    const automatedNotification = await this._dialog
      .open<
        NotificationDialogComponent,
        IAutomatedNotificationDialogData,
        IAutomatedNotificationReturnData
      >(
        NotificationDialogComponent,
        DialogPresets.large({
          height: '80%',
          data,
        })
      )
      .afterClosed()
      .toPromise();
    return this._toNotificationAutomation(
      event,
      scopeData,
      triggerAfterDate,
      automatedNotification
    );
  }

  async openAutomatedFormIssueAdd(
    event: IEvent,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<IAutomatedFormIssue> | undefined> {
    const formIssue = await this.openAutomatedFormIssueDialog({
      triggerDate: event.from,
      useRelativeTime: true,
    });
    return this._toAutomatedFormIssueAutomation(
      event,
      triggerAfterDate,
      formIssue
    );
  }

  async openAutomatedFormIssueEdit(
    formIssue: IAutomatedFormIssue,
    event: IEvent,
    triggerDate?: Timestamp,
    triggerAfterDate?: Timestamp,
    useRelativeTime: boolean = true
  ): Promise<IAutomation<IAutomatedFormIssue> | undefined> {
    const automatedFormIssue = await this.openAutomatedFormIssueDialog({
      formIssue,
      triggerDate,
      useRelativeTime,
    });
    return this._toAutomatedFormIssueAutomation(
      event,
      triggerAfterDate,
      automatedFormIssue
    );
  }

  openFromTemplateDialog$(
    practiceRef: DocumentReference<IPractice>,
    event: IEvent,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp
  ): Observable<IAutomation<AnyAutomation> | undefined> {
    return from(this._getAutomationConfigs(practiceRef)).pipe(
      switchMap((request) => this.selectConfiguration(request)),
      withLatestFrom(this._organisation.staffer$.pipe(filterUndefined())),
      concatMap(async ([response, staffer]) => {
        if (!response) {
          return;
        }
        const brand = await snapshot(
          this._organisation.brand$.pipe(filterUndefined())
        );
        return this._createFromConfig(
          response.config,
          event,
          staffer,
          brand.ref,
          scopeData,
          triggerAfterDate
        );
      })
    );
  }

  async openPreviewDialog<Resource extends IAutomationResource>(
    automation: IAutomation<Resource>
  ): Promise<void> {
    const data = await this._buildPreviewData(automation);
    if (!data) {
      this._snackBar.open(
        'No preview available for this automation.',
        'Close',
        { duration: 3000 }
      );
      return;
    }

    this._dialog.open<
      TemplatePreviewDialogComponent,
      ITemplatePreviewDialogData
    >(
      TemplatePreviewDialogComponent,
      DialogPresets.large({
        height: '80%',
        data,
      })
    );
  }

  private async _buildPreviewData<Resource extends IAutomationResource>(
    automation: IAutomation<Resource>
  ): Promise<ITemplatePreviewDialogData | undefined> {
    if (isGeneratedTask(automation.data)) {
      return {
        type: TemplateType.Html,
        context: {},
        template: automation.data.comment,
      };
    }

    if (isAutomatedNotification(automation.data)) {
      if (!automation.configRef) {
        return;
      }
      const config = await Firestore.getDoc(automation.configRef);
      if (!isAutomatedNotificationConfiguration(config)) {
        return;
      }

      const rawTemplate = await TemplateDefinition.resolveRawTemplate(
        await Firestore.getDoc(config.templateRef),
        {}
      );

      return {
        type:
          automation.data.type === AutomatedNotificationType.SMS
            ? TemplateType.PlainText
            : TemplateType.Html,
        context: {},
        template:
          automation.data.customTemplate?.content ?? rawTemplate.content,
      };
    }
  }

  private async _toNotificationAutomation(
    event: IEvent,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp,
    automatedNotification?: IAutomatedNotificationReturnData
  ): Promise<IAutomation<IAutomatedNotification> | undefined> {
    if (!automatedNotification) {
      return;
    }
    const staffer = await snapshotDefined(this._organisation.staffer$);
    const brand = await snapshotDefined(this._organisation.brand$);
    const automation = await AutomatedNotification.toAutomation(
      automatedNotification,
      {
        ...stafferToNamedDoc(staffer),
        type: AutomationCreator.Staffer,
      },
      event,
      brand.ref,
      scopeData,
      automatedNotification.configRef
    );
    if (automatedNotification.triggerDate) {
      automation.triggerDate = automatedNotification.triggerDate;
    }

    automation.triggerAfterDate = triggerAfterDate;
    return automation;
  }

  private async _toTaskAutomation(
    event: IEvent,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp,
    generatedTask?: IGeneratedTaskReturnData
  ): Promise<IAutomation<IGeneratedTask> | undefined> {
    if (!generatedTask) {
      return;
    }
    const staffer = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    const brand = await snapshot(
      this._organisation.brand$.pipe(filterUndefined())
    );
    const automation = await GeneratedTask.toAutomation(
      generatedTask,
      {
        ...stafferToNamedDoc(staffer),
        type: AutomationCreator.Staffer,
      },
      event,
      brand.ref,
      scopeData
    );
    if (generatedTask.triggerDate) {
      automation.triggerDate = generatedTask.triggerDate;
    }
    automation.triggerAfterDate = triggerAfterDate;
    return automation;
  }

  private async _toAutomatedFormIssueAutomation(
    event: IEvent,
    triggerAfterDate?: Timestamp,
    automatedFormIssue?: IAutomatedFormIssueReturnData
  ): Promise<IAutomation<IAutomatedFormIssue> | undefined> {
    if (!automatedFormIssue) {
      return;
    }
    const staffer = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    const brand = await snapshot(
      this._organisation.brand$.pipe(filterUndefined())
    );
    const automation = await AutomatedFormIssue.toAutomation(
      automatedFormIssue,
      {
        ...stafferToNamedDoc(staffer),
        type: AutomationCreator.Staffer,
      },
      event,
      brand.ref,
      automatedFormIssue.configRef
    );
    if (automatedFormIssue.triggerDate) {
      automation.triggerDate = automatedFormIssue.triggerDate;
    }

    automation.triggerAfterDate = triggerAfterDate;
    return automation;
  }

  private async _getAutomationConfigs(
    practiceRef: DocumentReference<IPractice>
  ): Promise<ISelectAutomationTemplateRequest> {
    const brand = await snapshot(this._organisation.brand$);
    if (!brand) {
      return { configs: [] };
    }

    const allConfigs = await snapshot(AutomationConfiguration.all$(brand));
    const availableConfigs = allConfigs.filter((config) =>
      automationConfigIsAvailable(config, practiceRef)
    );
    const configs = sortBy(availableConfigs, (config) =>
      AutomationDisplay.getLabel(config)
    );

    return { configs };
  }

  private async _createFromConfig(
    config: WithRef<AnyAutomationConfiguration>,
    event: IEvent,
    staffer: WithRef<IStaffer>,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<AnyAutomation> | undefined> {
    const creator = {
      ...stafferToNamedDoc(staffer),
      type: AutomationCreator.Staffer,
    };

    if (
      isAutomatedNotificationConfiguration(config) &&
      isWithRef<IAutomatedNotificationConfiguration>(config)
    ) {
      return AutomatedNotification.generateFromConfig(
        config,
        creator,
        event,
        brandRef,
        scopeData,
        triggerAfterDate
      );
    }

    if (
      isGeneratedTaskConfiguration(config) &&
      isWithRef<IGeneratedTaskConfiguration>(config)
    ) {
      return GeneratedTask.generateFromConfig(
        config,
        creator,
        event,
        brandRef,
        scopeData,
        triggerAfterDate
      );
    }

    if (
      isAutomatedFormIssueConfiguration(config) &&
      isWithRef<IAutomatedFormIssueConfiguration>(config)
    ) {
      return AutomatedFormIssue.generateFromConfig(
        config,
        creator,
        event,
        brandRef,
        triggerAfterDate
      );
    }
  }
}

function automationConfigIsAvailable(
  config: IAutomationConfiguration,
  practiceRef: DocumentReference<IPractice>
): boolean {
  return (
    config.isActive &&
    AutomationConfiguration.isGlobal(config) &&
    AutomationConfiguration.isForPractice(config, practiceRef)
  );
}
